// components/invisalign/InvisalignResources.tsx
"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";

const InvisalignResources: React.FC = () => {
  const allResources = [
    {
      title: "Is Invisalign Right For Me?",
      url: "/invisalign/invisalign-right",
      image: "/images/Astoria-Invisalign-dentist-showing-clear-aligners-on-dental-model-to-patient-wearing-black-sweater-with-sequins-during-consultation.webp",
      alt: "Astoria Invisalign dentist showing clear aligners on dental model to patient wearing black sweater with sequins during consultation",
      description: "Discover if Invisalign is the right orthodontic solution for your unique needs and lifestyle."
    },
    {
      title: "Invisalign for Teens",
      url: "/invisalign/invisalign-for-teens",
      image: "/images/Smiling-teenage-girl-holding-clear-aligners-with-both-hands-outdoors-demonstrating-invisalign-for-teens-treatment-in-queens.webp",
      alt: "Smiling teenage girl holding clear aligners with both hands outdoors demonstrating Invisalign for teens treatment in Queens",
      description: "Learn about Invisalign Teen and how clear aligners can help teenagers achieve a confident smile."
    },
    {
      title: "Invisalign for Brides",
      url: "/invisalign/invisalign-for-brides",
      image: "/images/Smiling-bride-in-white-dress-and-veil-holding-pink-and-red-bouquet-showing-perfect-teeth-after-Invisalign-treatment-for-wedding-day.webp",
      alt: "Smiling bride in white dress and veil holding pink and red bouquet showing perfect teeth after Invisalign treatment for wedding day",
      description: "Perfect your smile for your special day with Invisalign treatment designed for busy brides."
    },
    {
      title: "Invisalign for Travelers",
      url: "/invisalign/invisalign-for-travelers",
      image: "/images/Young-woman-in-orange-hoodie-and-sunglasses-laughing-outdoors-while-traveling-showing-confidence-from-invisalign-in-queens-treatment.webp",
      alt: "Young woman in orange hoodie and sunglasses laughing outdoors while traveling showing confidence from Invisalign in Queens treatment",
      description: "Straighten your teeth while maintaining your active travel lifestyle with convenient Invisalign."
    },
    {
      title: "Invisalign FAQs",
      url: "/invisalign/faqs",
      image: "/images/dentist-wearing-blue-scrubs-and-white-gloves-holding-two-clear-aligners-in-the-shape-of-a-heart.webp",
      alt: "Dentist wearing blue scrubs and white gloves holding two clear aligners in the shape of a heart",
      description: "Get answers to the most common questions about Invisalign treatment and what to expect."
    },
    {
      title: "Clear Aligners Before and After Results",
      url: "/invisalign/before-after",
      image: "/images/Side-by-side-images-of-young-woman-before-and-after-invisalign-showing-gap-closure-and-improved-smile.webp",
      alt: "Side-by-side images of young woman before and after Invisalign showing gap closure and improved smile",
      description: "See real transformation results from our Invisalign patients and their smile journeys."
    },
    {
      title: "Invisalign Videos",
      url: "/invisalign/invisalign-videos",
      image: "/images/Close-up-of-hand-placing-clear-aligner-into-black-case-on-blue-background-showing-how-to-store-aligners.webp",
      alt: "Close-up of hand placing clear aligner into black case on blue background showing how to store aligners",
      description: "Watch educational videos about Invisalign care, maintenance, and treatment process."
    },
    {
      title: "Invisalign Costs",
      url: "/invisalign/invisalign-costs",
      image: "/images/Smiling-woman-holding-clear-aligner-close-to-camera-as-Astoria-Invisalign-dentist-demonstrates-how-aligners-look-and-fit-during-treatment.webp",
      alt: "Smiling woman holding clear aligner close to camera as Astoria Invisalign dentist demonstrates how aligners look and fit during treatment",
      description: "Understand Invisalign pricing, insurance coverage, and flexible payment options available."
    },
    {
      title: "Invisalign vs Direct-To-Consumer Aligners",
      url: "/invisalign/invisalign-vs-consumer",
      image: "/images/Close-up-of-woman-holding-clear-aligner-in-foreground-while-smiling-blurred-in-background-demonstrating-benefits-of-invisalign.webp",
      alt: "Close-up of woman holding clear aligner in foreground while smiling blurred in background demonstrating benefits of Invisalign",
      description: "Compare professional Invisalign treatment with direct-to-consumer aligner options."
    },
    {
      title: "Invisalign vs Braces",
      url: "/invisalign/invisalign-vs-braces",
      image: "/images/Close-up-of-smiling-woman-showing-white-straight-teeth-with-background-image-of-braces-highlighting-difference-between-invisalign-and-traditional-braces.webp",
      alt: "Close-up of smiling woman showing white straight teeth with background image of braces highlighting difference between Invisalign and traditional braces",
      description: "Compare the benefits and differences between Invisalign clear aligners and traditional braces."
    }
  ];

  return (
    <section className="w-full py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Learn More About Invisalign
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] mx-auto mb-6"></div>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Invisalign is a powerful, flexible solution for straightening teeth without metal braces, and Smile4Me Dental Care offers a wide range of resources to help you understand how treatment works, who it&#39;s for, and what to expect at every stage.
          </p>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {allResources.map((resource, index) => (
            <Link
              key={index}
              href={resource.url}
              className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2"
            >
              {/* Image Container */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={resource.image}
                  alt={resource.alt}
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#AE7D2F] transition-colors duration-300">
                  {resource.title}
                </h3>
                
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {resource.description}
                </p>

                {/* Read More Link */}
                <div className="flex items-center text-[#AE7D2F] font-semibold group-hover:text-[#ECD05B] transition-colors duration-300">
                  <span className="mr-2">Learn More</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>
          ))}
        </div>

      </div>
    </section>
  );
};

export default InvisalignResources;
